import type { Article } from '../../../types/article'
import styles from './ArticleCard.module.css'

interface ArticleCardProps {
  article: Article
  showPublishBadge?: boolean
}

/* ── Icône calendrier SVG inline ─────────────── */
const IconCalendar = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={styles.metaIcon}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

/* ── Icône flèche externe SVG inline ─────────── */
const IconArrow = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={styles.arrowIcon}
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
)

/**
 * Formate une date ISO en format lisible : "12 mars 2025"
 */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export function ArticleCard({ article, showPublishBadge = false }: ArticleCardProps) {
  const { title, slug, categories, tags, publishedAt, cover, isPublished } = article

  // Use first category or default
  const category = categories?.[0] || 'Général'

  /*
   * URL utilise seulement le slug pour une URL propre.
   * Format : /blog/<slug>
   */
  const articleUrl = `/blog/${slug}`

  return (
    <article className={styles.card} aria-labelledby={`article-title-${slug}`}>
      {/* ── En-tête : catégorie + date ── */}
      <header className={styles.cardHeader}>
        {/* Badge de catégorie */}
        <span className={styles.category} aria-label={`Catégorie : ${category}`}>
          {category}
        </span>

        <div className={styles.cardHeaderRight}>
          {/* Badge statut publication (utilisateur connecté) */}
          {showPublishBadge && (
            <span className={isPublished ? styles.badgePublished : styles.badgeUnpublished}>
              {isPublished ? 'Publié' : 'Non publié'}
            </span>
          )}

          {/* Date de publication */}
          <time
            className={styles.date}
            dateTime={publishedAt}
            aria-label={`Publié le ${formatDate(publishedAt)}`}
          >
            <IconCalendar />
            {formatDate(publishedAt)}
          </time>
        </div>
      </header>

      {/* ── Image de couverture ── */}
      {cover && (
        <div className={styles.coverContainer}>
          <img
            src={cover}
            alt={`Couverture de ${title}`}
            className={styles.cover}
            loading="lazy"
          />
        </div>
      )}

      {/* ── Titre ── */}
      <h3 id={`article-title-${slug}`} className={styles.title}>
        <a
          href={articleUrl}
          className={styles.titleLink}
          /*
           * Toute la card est cliquable via le pseudo-élément ::after sur le lien,
           * mais on conserve le lien natif sur le titre pour l'accessibilité
           * (screen readers, navigation clavier).
           */
        >
          {title}
        </a>
      </h3>

      {/* ── Extrait ── */}
      <p className={styles.excerpt}>{article.description}</p>

      {/* ── Tags ── */}
      {tags.length > 0 && (
        <ul className={styles.tags} aria-label="Tags">
          {tags.map((tag) => (
            <li key={tag} className={styles.tag}>
              #{tag}
            </li>
          ))}
        </ul>
      )}

      {/* ── CTA Lire l'article ── */}
      <div className={styles.footer}>
        <a
          href={articleUrl}
          className={styles.readMore}
          aria-label={`Lire l'article : ${title}`}
          tabIndex={-1}
          /*
           * tabIndex="-1" car le lien du titre couvre déjà la navigation clavier.
           * Le ::after de .titleLink rend toute la card cliquable à la souris.
           */
        >
          Lire l'article
          <IconArrow />
        </a>
      </div>
    </article>
  )
}